import {
  Button,
  CircularProgress,
  FormControlLabel,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material';
import { Film } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { VideoJob } from '../types';
import { VideoJobCard } from './VideoJobCard';

interface VideoJobListProps {
  jobs: VideoJob[];
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onCancel: (jobId: string) => Promise<void>;
}

export const VideoJobList: React.FC<VideoJobListProps> = ({
  jobs,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [hideFailed, setHideFailed] = useState(false);
  const visibleJobs = hideFailed ? jobs.filter((job) => job.status !== 'failed') : jobs;

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        {Array.from({ length: 2 }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={260} />
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="flex min-h-[280px] flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-gray-300 px-6 text-center dark:border-slate-700">
        <Film size={36} className="text-gray-400 dark:text-slate-500" />
        <Typography className="text-gray-500 dark:text-slate-400">
          {t('services.generateVideo.empty')}
        </Typography>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={hideFailed}
            onChange={(event) => setHideFailed(event.target.checked)}
          />
        }
        label={t('services.generateVideo.hideFailed')}
      />
      <div className="grid grid-cols-1 gap-4">
        {visibleJobs.map((job) => (
          <VideoJobCard key={job.jobId} job={job} onCancel={onCancel} />
        ))}
      </div>
      {hasMore && (
        <div className="flex justify-center">
          <Button
            onClick={onLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={16} /> : undefined}
          >
            {t('services.generateVideo.loadMore')}
          </Button>
        </div>
      )}
    </div>
  );
};
